import { type RecoilState, useRecoilCallback, useRecoilValue, waitForAll } from 'recoil';
import { blockSettings, selectedBlocksState } from 'venn-state';
import { useMemo } from 'react';
import { uniq } from 'lodash';
import { hasDisabledSubjectInputs, MAX_BLOCK_SUBJECT_INPUTS } from 'venn-utils';

type InputState<T> = {
  state: 'checked' | 'unchecked' | 'indeterminate';
  id: T;
  disabled: boolean;
  tooltip: string | undefined;
};

const SUBJECT_LIMIT_MESSAGE = `Blocks only support up to ${MAX_BLOCK_SUBJECT_INPUTS} subject groups`;

export const useMultiInputSelection = <T>(
  inputIdsState: RecoilState<T[]>,
  blockInputState: (id: string) => RecoilState<T[]>,
) => {
  const selectedBlocks = useRecoilValue(selectedBlocksState);
  const inputIds = useRecoilValue(inputIdsState);
  const blockInputSelections = useRecoilValue(waitForAll(selectedBlocks.map((id) => blockInputState(id))));
  const allBlockSettings = useRecoilValue(waitForAll(selectedBlocks.map((id) => blockSettings(id))));
  const selectionDoesNotSupportSubjects = allBlockSettings.some((setting) =>
    hasDisabledSubjectInputs(setting.customBlockType),
  );

  const inputsStates = useMemo((): InputState<T>[] => {
    const usedInputs = new Set(blockInputSelections.flat());
    const noBlocksSelected = selectedBlocks.length === 0;
    const someBlockAtLimit = blockInputSelections.some(
      (blockInputIds) => blockInputIds.length >= MAX_BLOCK_SUBJECT_INPUTS,
    );
    return inputIds.map((id) => {
      if (!usedInputs.has(id)) {
        return {
          state: 'unchecked',
          id,
          disabled: noBlocksSelected || someBlockAtLimit || selectionDoesNotSupportSubjects,
          tooltip: someBlockAtLimit ? SUBJECT_LIMIT_MESSAGE : undefined,
        };
      }
      const isChecked = blockInputSelections.every((blockInputIds) => blockInputIds.includes(id));
      return {
        state: isChecked ? 'checked' : 'indeterminate',
        id,
        disabled: isChecked ? selectionDoesNotSupportSubjects : someBlockAtLimit || selectionDoesNotSupportSubjects,
        tooltip: !isChecked && someBlockAtLimit ? SUBJECT_LIMIT_MESSAGE : undefined,
      };
    });
  }, [blockInputSelections, inputIds, selectedBlocks.length, selectionDoesNotSupportSubjects]);

  const onInputSelect = useRecoilCallback(
    ({ set }) =>
      (inputId: T, newState: boolean | 'indeterminate') => {
        const isChecked = newState === true;
        if (isChecked) {
          selectedBlocks.forEach((blockId) => set(blockInputState(blockId), (current) => uniq([...current, inputId])));
        } else {
          selectedBlocks.forEach((blockId) =>
            set(blockInputState(blockId), (current) => current.filter((id) => id !== inputId)),
          );
        }
      },
    [selectedBlocks, blockInputState],
  );

  return { inputsStates, onInputSelect };
};
